import type { LucideIcon } from 'lucide-react';
import { BarChart3, ClipboardList, FileText, Home, Landmark, Settings, ShieldCheck } from 'lucide-react';
import { allowedRoles } from 'enums/Role/allowedRoles';
import { normalizeRole } from 'helpers/roles';

const {
	Super_Admin: SA,
	Director_de_Informática: DI,
	Director_de_Bienes_Fiscales: DBF,
	Operador_de_Dirección_de_Bienes_Fiscales: ODBF,
	Encargado_Patrimonial: EP,
	Operador_Dependencia_Patrimonial: ODP
} = allowedRoles;

export interface NavItem {
	key: string;
	label: string;
	to: string;
	/** Roles que ven la opción; sin lista la ve cualquier usuario autorizado. Super_Admin siempre la ve. */
	roles?: string[];
}

export interface NavLinkEntry extends NavItem {
	icon: LucideIcon;
}

export interface NavGroup {
	key: string;
	label: string;
	icon: LucideIcon;
	roles: string[];
	items: NavItem[];
}

export type NavEntry = NavLinkEntry | NavGroup;

export const isNavGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry;

// Misma visibilidad que el menú anterior (`ToolBar.tsx`); la tabla rol × opción está en
// `openspec/changes/adopcion-shell-safi/inventario-navegacion.md`. Grupo e ítem se combinan con AND.
export const NAVIGATION: NavEntry[] = [
	{ key: 'inicio', label: 'Inicio', to: '/home', icon: Home },
	{
		key: 'registro',
		label: 'Registro de Movimientos',
		icon: ClipboardList,
		roles: [EP, ODP, SA],
		items: [
			{ key: 'alta', label: 'Alta', to: '/alta' },
			{ key: 'baja', label: 'Baja', to: '/baja' }
		]
	},
	{
		key: 'gestion',
		label: 'Gestión de Movimientos',
		icon: Landmark,
		roles: [ODBF, EP, ODP, SA],
		items: [
			{ key: 'altas', label: 'Altas', to: '/movimientos/altas' },
			{ key: 'bajas', label: 'Bajas', to: '/movimientos/bajas' }
		]
	},
	{
		key: 'planilla',
		label: 'Planilla Mensual',
		icon: FileText,
		roles: [EP, DBF, ODBF, SA],
		items: [
			{ key: 'planilla-auditar', label: 'Auditar', to: '/planilla-mensual-auditoria', roles: [DBF, ODBF, SA] },
			{ key: 'planilla-generar', label: 'Generación', to: '/planilla-mensual-generar', roles: [EP, SA] },
			{ key: 'planilla-gestionar', label: 'Gestionar', to: '/planilla-mensual-gestionar', roles: [EP, SA] }
		]
	},
	{
		key: 'balances',
		label: 'Balances',
		icon: BarChart3,
		roles: [DBF, ODBF, SA],
		items: [
			// El ítem lista a Director de Informática, pero el grupo no: ese rol no lo ve (igual que antes)
			{ key: 'cierre', label: 'Cierre de Ejercicio', to: '/balance/cierre-ejercicio', roles: [DBF, DI, ODBF, SA] },
			{ key: 'resumen', label: 'Balance Anual', to: '/balance/resumen', roles: [DBF, ODBF, SA] }
		]
	},
	{
		key: 'configuracion',
		label: 'Configuración',
		icon: Settings,
		roles: [DI, DBF, SA],
		items: [
			{ key: 'usuarios', label: 'Usuarios', to: '/configuracion/usuarios', roles: [DI, SA] },
			{ key: 'parametros', label: 'Parámetros', to: '/configuracion/parametros', roles: [DI, DBF, SA] }
		]
	},
	// La ruta `/auditoria` no existe en `Views.tsx` (cae en `*` y redirige a `/home`): se conserva el destino anterior
	{ key: 'auditoria', label: 'Auditoría', to: '/auditoria', icon: ShieldCheck, roles: [SA] }
];

// Super_Admin ve todo; el resto necesita algún rol de la lista. Los nombres se comparan normalizados.
const canSee = (userRoles: string[], roles?: string[]) => {
	if (!roles) return true;
	const normalized = userRoles.map(normalizeRole);
	if (normalized.includes(normalizeRole(SA))) return true;
	return roles.some((role) => normalized.includes(normalizeRole(role)));
};

/** Navegación que corresponde a los roles dados: sin opciones no permitidas ni grupos vacíos. */
export const getVisibleNavigation = (userRoles: string[], entries: NavEntry[] = NAVIGATION): NavEntry[] =>
	entries.flatMap((entry): NavEntry[] => {
		if (!canSee(userRoles, entry.roles)) return [];
		if (!isNavGroup(entry)) return [entry];
		const items = entry.items.filter((item) => canSee(userRoles, item.roles));
		return items.length > 0 ? [{ ...entry, items }] : [];
	});

/** La ruta pertenece a la opción si es la misma o cuelga de ella (`/alta/editar/3` resalta Alta). */
export const isActivePath = (pathname: string, to: string) => pathname === to || pathname.startsWith(`${to}/`);
